// Entry-bucket contradiction detection for the manual condition picker.
//
// Entry conditions in one bucket are ANDed — they must ALL fire on the SAME bar to open a
// position. So two conditions from the same indicator instance that can never be simultaneously
// true make the bucket unsatisfiable (it will never enter). We surface that as a NON-BLOCKING
// warning — a 0-trade backtest is also a valid teaching signal, so we never hard-block.
//
// Detection is by condition-id SUFFIX, not by bias. Bias is the wrong key: e.g. CMO "os" (< -50,
// tagged bullish) and "neg" (< 0, tagged bearish) are BOTH true at -60, so a bias-opposition check
// would false-positive. Suffix pairs are derived from the actual condition generators and encode
// genuine value-range disjointness (e.g. rsi oversold <30 vs strong 50–70). The condition `id` is
// `{instanceKey}_{suffix}` (some add a column segment, e.g. `{instanceKey}_{col}_price_above`), so
// we compare the id's trailing suffix tokens within one indicator instance.

import type { ConditionPreviewEntry } from "../../../api/conditions";

export interface Contradiction {
  /** The two condition names that conflict. */
  names: [string, string];
  /** The indicator instance they share, e.g. "rsi_0". */
  indicatorInstanceKey: string;
}

// Mutually-exclusive suffix groups — at most ONE member of a group can be true on a given bar.
// Each entry is matched against the END of a condition id (so column-segmented ids like
// `donchian_0_donchian_upper_price_above` still match the `price_above`/`price_below` pair).
// Verified against the generators in TraderxAlerts.IndicatorEngine/Services/Conditions/Generators.
const MUTEX_SUFFIX_GROUPS: string[][] = [
  // RSI bands (disjoint): <30 / 30–50 / 50–70 / >70
  ["oversold", "weak", "strong", "overbought"],
  // Sign / zero-cross families shared by MACD line, CMO, TRIX, Fisher, ROC, Momentum
  ["pos", "neg"],
  ["bull_cross", "bear_cross"],
  ["above_sig", "below_sig"],
  ["strong_bull", "strong_bear"],
  ["extreme_bull", "extreme_bear"],
  // Oscillator OB/OS extremes (Stoch %K/%D, Williams %R, CMO)
  ["os", "ob"],
  ["mid_bull", "mid_bear"],
  // Price-vs-line / trend-flip families (MA, DEMA, Keltner, Donchian, Supertrend, PSAR, VWAP)
  ["price_above", "price_below"],
  ["above", "below"],
  ["bull_flip", "bear_flip"],
  ["expanding", "contracting"],
  // Bar-over-bar direction — a value can't be both rising and falling on the same bar
  ["rising", "falling"],
];

/** Which mutex group (by index) a condition id belongs to, or -1 if none. Matches by id suffix. */
function groupOf(id: string): number {
  for (let g = 0; g < MUTEX_SUFFIX_GROUPS.length; g++) {
    for (const suffix of MUTEX_SUFFIX_GROUPS[g]!) {
      // Suffix match on a token boundary: id ends with `_{suffix}` (or equals it).
      if (id === suffix || id.endsWith(`_${suffix}`)) return g;
    }
  }
  return -1;
}

/**
 * Find unsatisfiable AND-combos among the SELECTED indices of one entry bucket. Two selected
 * conditions conflict when they share the same indicator instance AND fall in the same mutex group.
 * @param selectedIndices the bucket's currently-ticked condition indices
 * @param conditions the full flat preview list (its `index` values align with selectedIndices)
 */
export function findContradictions(
  selectedIndices: number[],
  conditions: ConditionPreviewEntry[],
): Contradiction[] {
  if (selectedIndices.length < 2) return [];

  const byIndex = new Map(conditions.map((c) => [c.index, c]));
  const selected = selectedIndices
    .map((i) => byIndex.get(i))
    .filter((c): c is ConditionPreviewEntry => c !== undefined);

  const out: Contradiction[] = [];
  // O(n²) over a tiny selection — clearer than bucketing, and selections are small.
  for (let a = 0; a < selected.length; a++) {
    for (let b = a + 1; b < selected.length; b++) {
      const ca = selected[a]!;
      const cb = selected[b]!;
      if (ca.indicatorInstanceKey !== cb.indicatorInstanceKey) continue;
      const ga = groupOf(ca.id);
      if (ga === -1) continue;
      if (ga === groupOf(cb.id)) {
        out.push({ indicatorInstanceKey: ca.indicatorInstanceKey, names: [ca.name, cb.name] });
      }
    }
  }
  return out;
}
